// Shared calendar-event helpers. Isomorphic: the Express route uses these to
// filter what a caller may see, the React calendar uses them to lay events out
// on a month grid — so both agree on visibility and on which days an event spans.

import { isValidYMD, timeToMinutes } from './date-utils'

export interface CalendarEvent {
  id: string
  title: string
  description?: string | null
  type: string
  /** YYYY-MM-DD. */
  startDate: string
  /** YYYY-MM-DD, inclusive; equals startDate for a single-day event. */
  endDate: string
  /** HH:MM, optional — an event with no time is all-day. */
  startTime?: string | null
  endTime?: string | null
  location?: string | null
  audience: string
  /** userIds; only meaningful when audience is 'SELECTED'. */
  attendees?: string[] | null
  createdBy?: string | null
  createdAt?: string
  updatedAt?: string
}

/**
 * Whether `userId` may see this event. Admin/HR see everything; everyone else
 * sees company-wide events plus the ones they are named on.
 */
export function canSeeEvent(event: Pick<CalendarEvent, 'audience' | 'attendees'>, userId: string, role?: string): boolean {
  if (role === 'ADMIN' || role === 'HR') return true
  if (String(event.audience ?? '').toUpperCase() === 'ALL') return true
  return Array.isArray(event.attendees) && event.attendees.includes(userId)
}

/** Every YYYY-MM-DD an event covers, inclusive. Empty for a malformed range. */
export function eventDays(event: Pick<CalendarEvent, 'startDate' | 'endDate'>): string[] {
  const { startDate, endDate } = event
  if (!isValidYMD(startDate) || !isValidYMD(endDate) || endDate < startDate) return []
  const out: string[] = []
  for (
    let d = new Date(startDate + 'T00:00:00Z');
    d <= new Date(endDate + 'T00:00:00Z');
    d = new Date(d.getTime() + 86_400_000)
  ) {
    out.push(d.toISOString().slice(0, 10))
  }
  return out
}

/** Does the event touch [rangeStart, rangeEnd]? Both bounds inclusive. */
export function eventOverlapsRange(
  event: Pick<CalendarEvent, 'startDate' | 'endDate'>,
  rangeStart: string,
  rangeEnd: string
): boolean {
  if (!event.startDate || !event.endDate) return false
  return event.startDate <= rangeEnd && event.endDate >= rangeStart
}

/** Index events by the days they cover, for O(1) month-grid lookups. */
export function eventsByDay(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  const out: Record<string, CalendarEvent[]> = {}
  for (const e of events) {
    for (const day of eventDays(e)) {
      if (!out[day]) out[day] = []
      out[day].push(e)
    }
  }
  for (const day of Object.keys(out)) out[day].sort(compareEvents)
  return out
}

/** All-day events first, then by start time, then by title. */
export function compareEvents(a: CalendarEvent, b: CalendarEvent): number {
  const at = timeToMinutes(a.startTime)
  const bt = timeToMinutes(b.startTime)
  if (at === null && bt !== null) return -1
  if (at !== null && bt === null) return 1
  if (at !== null && bt !== null && at !== bt) return at - bt
  return (a.title || '').localeCompare(b.title || '')
}

/** "10:00 AM – 11:30 AM", "10:00 AM", or "All day". */
export function formatEventTime(event: Pick<CalendarEvent, 'startTime' | 'endTime'>): string {
  const fmt = (t?: string | null) => {
    const mins = timeToMinutes(t)
    if (mins === null) return null
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
  }
  const start = fmt(event.startTime)
  if (!start) return 'All day'
  const end = fmt(event.endTime)
  return end ? `${start} – ${end}` : start
}
